import { useCallback, useEffect, useRef } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, Circle, Marker, Polyline, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./drivers.css";

export const KINDS = {
  truck_parking: ["Truck parking", "#FF5722", "P"], truck_stop: ["Truck stop", "#0A234A", "T"], truck_fuel: ["Truck fuel", "#059669", "F"],
  rest_area: ["Rest area", "#2563EB", "R"], service_plaza: ["Service plaza", "#7C3AED", "S"],
};
export const PLACE = ["Place", "#475569", "•"];
export const USES = { truck_parking: ["park"], truck_stop: ["park", "rest"], truck_fuel: [], rest_area: ["rest", "park"], service_plaza: ["rest", "park"] };
export const USE_LABEL = { park: ["PARK", "bg-orange-100 text-[#C2410C] border-orange-200"], rest: ["REST", "bg-blue-100 text-blue-700 border-blue-200"] };

export function UseTags({ kind, testid }) {
  return (
    <span className="inline-flex gap-1" data-testid={testid}>
      {(USES[kind] || []).map((u) => <span key={u} className={`rounded border px-1.5 py-px font-mono text-[9px] font-bold tracking-wider ${USE_LABEL[u][1]}`}>{USE_LABEL[u][0]}</span>)}
    </span>
  );
}

export function Legend() {
  return (
    <div className="pointer-events-none absolute bottom-3 left-3 z-[500] hidden rounded-lg border border-slate-200 bg-white/95 px-3 py-2 shadow-md backdrop-blur lg:block" data-testid="drv-legend">
      {Object.entries(KINDS).map(([k, [label, color, letter]]) => (
        <div key={k} className="flex items-center gap-2 py-0.5 text-[11px] font-medium text-[#0A234A]">
          <span className="flex h-4 w-4 items-center justify-center rounded-full font-mono text-[9px] font-bold text-white" style={{ background: color }}>{letter}</span>{label}<span className="ml-auto pl-2"><UseTags kind={k} /></span>
        </div>
      ))}
      <p className="mt-1 text-[10px] text-slate-400">Numbers on pins = live open spaces</p>
    </div>
  );
}
const safeId = (id) => String(id).replace(/[^a-zA-Z0-9_-]/g, "-");

function pin(r, active) {
  const [, color, letter] = KINDS[r.kind] || ["", "#64748B", "P"];
  const label = r.available != null ? String(r.available) : letter;
  const s = active ? 1.3 : 1;
  return L.divIcon({
    className: "drv-pin-wrap", iconSize: [30 * s, 40 * s], iconAnchor: [15 * s, 40 * s],
    html: `<div data-testid="drv-marker-${safeId(r.id)}" class="drv-pin${active ? " is-active" : ""}${r.available === 0 ? " is-full" : ""}" style="width:${30 * s}px;height:${40 * s}px"><svg viewBox="0 0 30 40" width="100%" height="100%"><path d="M15 39C15 39 28 24.5 28 14.5A13 13 0 0 0 2 14.5C2 24.5 15 39 15 39Z" fill="${color}" stroke="#fff" stroke-width="2.2"/></svg><span>${label}</span></div>`,
  });
}

const meIcon = (live, rot) => L.divIcon({ className: "drv-pin-wrap", iconSize: [30, 30], iconAnchor: [15, 15], html: rot == null ? `<div data-testid="drv-marker-me" class="drv-me${live ? " is-live" : ""}"></div>` : `<div data-testid="drv-marker-me" class="drv-me-arrow" style="transform:rotate(${Math.round(rot)}deg)"><svg viewBox="0 0 30 30" width="30" height="30"><path d="M15 2 L26 27 L15 21 L4 27 Z" fill="#2563EB" stroke="#fff" stroke-width="2.5" stroke-linejoin="round"/></svg></div>` });
const destIcon = L.divIcon({ className: "drv-pin-wrap", iconSize: [18, 18], iconAnchor: [9, 9], html: `<div data-testid="drv-marker-dest" class="drv-dest"></div>` });
const viaIcon = (n) => L.divIcon({ className: "drv-pin-wrap", iconSize: [24, 24], iconAnchor: [12, 12], html: `<div data-testid="drv-marker-via-${n}" class="drv-via">${n}</div>` });
const STREET = { url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' };
const SAT = { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: "Imagery &copy; Esri, Maxar, Earthstar Geographics" };
const SAT_ROADS = "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}";
const SAT_LABELS = "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}";

function Camera({ origin, results, radius, route, pos, follow, active, recenter, rotated, padBottom }) {
  const map = useMap();
  const fitted = useRef("");
  const padRef = useRef(0);
  padRef.current = padBottom || 0;
  const centerOn = useCallback((lat, lon, zoom) => {
    const pad = padRef.current;
    if (!pad) { map.setView([lat, lon], zoom, { animate: true }); return; }
    const pt = map.project([lat, lon], zoom).add([0, pad / 2]);
    map.setView(map.unproject(pt, zoom), zoom, { animate: true });
  }, [map]);
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 50);
    return () => clearTimeout(t);
  }, [map, rotated]);
  useEffect(() => {
    if (route || !origin) return;
    const pts = results.slice(0, 25).map((r) => [r.lat, r.lon]);
    if (pts.length) map.fitBounds([[origin.lat, origin.lon], ...pts], { paddingTopLeft: [40, 80], paddingBottomRight: [40, 40 + padRef.current], maxZoom: 12 });
    else map.setView([origin.lat, origin.lon], radius > 100 ? 7 : radius > 50 ? 8 : 9);
  }, [map, origin, results, radius, route]);
  useEffect(() => {
    if (!route || fitted.current === route.generated_at) return;
    fitted.current = route.generated_at;
    if (!follow) map.fitBounds(route.coords, { paddingTopLeft: [50, 110], paddingBottomRight: [50, 50 + padRef.current] });
  }, [map, route, follow]);
  useEffect(() => {
    if (follow && pos) map.setView([pos.lat, pos.lon], Math.max(map.getZoom(), 16), { animate: true });
  }, [map, follow, pos]);
  useEffect(() => {
    if (active && !route) centerOn(active.lat, active.lon, Math.max(map.getZoom(), 13));
  }, [map, active, route, centerOn]);
  const posRef = useRef(null);
  posRef.current = pos || origin;
  useEffect(() => {
    const p = posRef.current;
    if (recenter && p) centerOn(p.lat, p.lon, Math.max(map.getZoom(), 14));
  }, [map, recenter, centerOn]);
  return null;
}

export const mapCredit = (satellite) => (satellite ? "Imagery © Esri, Maxar, Earthstar Geographics · Leaflet" : "© OpenStreetMap contributors · Leaflet");

export default function DriverMap({ origin, radius, results, focus, onPick, nav, follow, satellite, bearing, recenter, attribution = true, padBottom = 0 }) {
  const active = focus || nav.dest;
  const me = nav.pos || origin;
  const rotated = bearing != null;
  const heading = nav.pos?.heading;
  const meRot = nav.route && heading != null ? heading - (bearing || 0) : null;
  const base = satellite ? SAT : STREET;
  return (
    <div className="relative h-full w-full overflow-hidden bg-slate-200" data-testid="drv-map-canvas">
      <div className="absolute" style={{ inset: rotated ? "-30%" : 0, transform: rotated ? `rotate(${-bearing}deg)` : "none", transition: "transform 0.6s ease-out" }}>
        <MapContainer center={[origin.lat, origin.lon]} zoom={9} className="h-full w-full" scrollWheelZoom zoomControl={false} attributionControl={attribution && !rotated}>
          <TileLayer key={base.url} attribution={base.attribution} url={base.url} maxZoom={19} />
          {satellite && <TileLayer url={SAT_ROADS} maxZoom={19} />}
          {satellite && <TileLayer url={SAT_LABELS} maxZoom={19} />}
          {!nav.route && <Circle center={[origin.lat, origin.lon]} radius={radius * 1000} pathOptions={{ color: satellite ? "#fff" : "#0A234A", weight: 1, fillOpacity: 0.03 }} />}
          {nav.route && <Polyline positions={nav.route.coords} pathOptions={{ color: "#0A234A", weight: 11, opacity: 0.35 }} />}
          {nav.route && <Polyline positions={nav.route.coords} pathOptions={{ color: nav.route.truck_aware ? "#FF5722" : "#2563EB", weight: 6, opacity: 0.95 }} />}
          {results.map((r) => (
            <Marker key={r.id} position={[r.lat, r.lon]} icon={pin(r, active?.id === r.id)} zIndexOffset={active?.id === r.id ? 1000 : 0}
              eventHandlers={{ click: () => onPick(r) }} title={r.name || KINDS[r.kind]?.[0]} />
          ))}
          {active && !results.some((r) => r.id === active.id) && <Marker position={[active.lat, active.lon]} icon={destIcon} />}
          {(nav.via || []).map((v, i) => <Marker key={`via-${v.id}`} position={[v.lat, v.lon]} icon={viaIcon(i + 1)} zIndexOffset={1500} />)}
          <Marker position={[me.lat, me.lon]} icon={meIcon(!!nav.pos, meRot)} zIndexOffset={2000} />
          <Camera origin={origin} results={results} radius={radius} route={nav.route} pos={nav.pos} follow={follow} active={active} recenter={recenter} rotated={rotated} padBottom={padBottom} />
        </MapContainer>
      </div>
      {rotated && attribution && <span className="pointer-events-none absolute bottom-1 right-1 z-[400] rounded bg-white/80 px-1 text-[9px] text-slate-500">{satellite ? "Imagery © Esri, Maxar, Earthstar Geographics" : "© OpenStreetMap contributors"}</span>}
    </div>
  );
}
